//Helpers
import { postReq } from 'src/services/http';
import { getEvChargerStatistics } from './ev_charger_statistics_helpers';
//Types
import type { Device } from 'src/types/Device';
import type {
	EventsLogRequest,
	EventsLogResponse,
	EventsLogResultData,
} from 'src/types/requests/eventslog/events_log';
import type { SessionEvents } from './ev_charger_session_info_helpers';

export type SessionMode = 'charging' | 'plug';
export type EventLogData = {
	timestamp: number;
	state: string;
};

/**
 * Returns the session info events for the EV Charger with statistics.
 * @param device The device for which to get the session info events.
 * @param filterByCharging If true, the session spans from the first `charger_charging`
 * to the first transition out of charging. Handles missing `charger_free` (unplug).
 * @returns SessionEvent or null.
 */
export const getLastSessionEvents = async (
	device: Device,
	mode: SessionMode = 'plug',
): Promise<SessionEvents | null> => {
	const eventLogData: EventLogData[] = await getSessionEvents(device);
	if (eventLogData === null) return null;

	const eventsLog: SessionEvents[] = parseSessionEvents(eventLogData, mode);
	const lastSession: SessionEvents = eventsLog?.[0] || null;
	if (lastSession) {
		lastSession.statistics = await getEvChargerStatistics(
			device,
			lastSession.session_start,
			lastSession.session_end,
		);
	}
	return lastSession;
};

export const getSessionEvents = async (device: Device): Promise<EventLogData[]> => {
	const payload: EventsLogRequest = { tags: [device.id], limit: 100 };
	const res: EventsLogResponse = await postReq(
		'/statistics/event-log',
		payload,
		20000,
		'',
		true,
		true,
	);
	return res?.result ? parseEventsLog(device.id, res) : null;
};

/**
 * Returns EV-charger session events (newest→oldest).
 * @param device The device for which to get the session info events.
 * @param mode If true, use charging-only window (first charging → first non-charging).
 * Otherwise, use plug window (insert → free).
 * When `mode` is charging, also handles missing `charger_free` (unplug).
 * @returns SessionEvents[] (only sessions longer than 1 minute).
 */
export const parseSessionEvents = (
	eventLogData: EventLogData[],
	mode: SessionMode = 'plug',
): SessionEvents[] => {
	const eventsLog: SessionEvents[] = [];
	let sessionStart: number = null;
	let sessionEnd: number = null;
	let chargerStart: number = null;
	let chargerEnd: number = null;
	let chargerComplete: number = null;
	let chargerPlugged: number = null;
	let chargerUnplugged: number = null;

	const resetSession = () => {
		sessionStart = null;
		sessionEnd = null;
		chargerStart = null;
		chargerEnd = null;
		chargerPlugged = null;
		chargerUnplugged = null;
		chargerComplete = null;
	};
	const addSession = (sessionStart: number, sessionEnd: number) => {
		if (sessionEnd - sessionStart > 60 * 1000) {
			eventsLog.push({
				session_start: new Date(sessionStart),
				session_end: sessionEnd ? new Date(sessionEnd) : null,
				charger_start: chargerStart ? new Date(chargerStart) : null,
				charger_end: chargerEnd ? new Date(chargerEnd) : null,
				charger_plugged: chargerPlugged ? new Date(chargerPlugged) : null,
				charger_unplugged: chargerUnplugged ? new Date(chargerUnplugged) : null,
				charger_complete: chargerComplete ? new Date(chargerComplete) : null,
			});
		}
	};

	if (mode == 'charging') {
		// charger_charging - Charging (power delivery started)
		// charger_wait - Waiting (no power). Marks end if it follows `charger_charging`.
		// charger_free_fault | charger_fault - Fault; charging stopped
		// charger_end - Session completed successfully (full charge only)
		for (let i = 0; i < eventLogData.length; i++) {
			const { state, timestamp } = eventLogData[i];
			const prevState = eventLogData[i + 1]?.state || null;
			if (sessionEnd && /^(charger_charging)$/.test(state)) {
				sessionStart = chargerStart = timestamp;
				addSession(sessionStart, sessionEnd);
				resetSession();
			} else if (
				/^(charger_wait)$/.test(state) &&
				!/^(charger_insert|true|false|null)$/.test(prevState)
			) {
				chargerEnd = timestamp;
				if (/^(charger_charging)$/.test(prevState)) {
					sessionEnd = timestamp;
				}
			} else if (/^(charger_free|charger_free_fault|charger_fault|charger_end)$/.test(state)) {
				sessionEnd = chargerEnd = timestamp;
				if (/^(charger_end)$/.test(state)) {
					chargerComplete = timestamp;
				}
			}
		}
	} else {
		// charger_insert - Plugged in; start new session
		// charger_free - Unplugged (car plug not inserted); end session
		// charger_end - Session completed successfully (full charge; not unplug)
		for (let i = 0; i < eventLogData.length; i++) {
			const { state, timestamp } = eventLogData[i];
			if (sessionEnd && /^(charger_insert)$/.test(state)) {
				sessionStart = chargerPlugged = timestamp;
				addSession(sessionStart, sessionEnd);
				resetSession();
			} else if (/^(charger_free)$/.test(state)) {
				sessionEnd = chargerUnplugged = timestamp;
			} else if (/^(charger_end)$/.test(state)) {
				chargerComplete = timestamp;
			}
		}
	}

	return eventsLog;
};

const parseEventsLog = (deviceID: Device['id'], res: EventsLogResponse): EventLogData[] => {
	const data = (res?.result?.[deviceID] || []) as EventsLogResultData[];
	const events = [];
	for (const ev of data) {
		let state: string | undefined;
		try {
			const parsedPayload = JSON.parse(ev.p);
			state = parsedPayload?.[2];
		} catch {
			continue;
		}
		events.push({
			timestamp: ev.t,
			state,
		});
	}
	return events;
};
